// src/components/AvatarWithInitials.jsx

import React from 'react';

/**
 * Componente de avatar inteligente que muestra:
 * - Imagen si el usuario tiene urlAvatar
 * - Iniciales con color generado si no tiene imagen
 * 
 * @param {Object} props
 * @param {Object} props.usuario - Objeto con datos del usuario
 * @param {string} props.usuario.nombrePropietario - Nombre completo del usuario
 * @param {string} props.usuario.urlAvatar - URL del avatar (opcional)
 * @param {number} props.size - Tamaño del avatar en pixels (default: 40)
 * @param {string} props.fontSize - Tamaño de fuente para iniciales (default: calculado automáticamente)
 * @param {string} props.className - Clases CSS adicionales
 * @param {Function} props.onClick - Callback al hacer clic
 */
export default function AvatarWithInitials({ 
  usuario, 
  size = 40, 
  fontSize,
  className = '',
  onClick 
}) {
  /**
   * Extrae las iniciales del nombre
   * - Si tiene 1 palabra: primeras 2 letras (ej: "Juan" → "JU")
   * - Si tiene 2+ palabras: primera letra de cada una (ej: "Juan Pérez" → "JP")
   */
  const getInitials = (nombre) => {
    if (!nombre) return 'U';
    
    const nombreLimpio = nombre.trim();
    const palabras = nombreLimpio.split(' ').filter(p => p.length > 0);
    
    if (palabras.length === 0) return 'U';
    if (palabras.length === 1) {
      // Solo una palabra: tomar primeras 2 letras
      return nombreLimpio.substring(0, 2).toUpperCase();
    }
    
    // Dos o más palabras: primera letra de las primeras 2
    return (palabras[0][0] + palabras[1][0]).toUpperCase();
  };

  /**
   * Genera un color consistente basado en el nombre
   * Siempre el mismo nombre generará el mismo color
   */
  const getColorFromName = (nombre) => {
    if (!nombre) return '#95a5a6'; // Gris por defecto
    
    // Generar hash del nombre
    let hash = 0;
    for (let i = 0; i < nombre.length; i++) {
      hash = nombre.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Paleta de colores profesionales y accesibles (buen contraste con blanco)
    const colores = [
      '#1abc9c', // Turquesa
      '#2ecc71', // Verde
      '#3498db', // Azul
      '#9b59b6', // Morado
      '#34495e', // Azul oscuro
      '#16a085', // Verde azulado
      '#27ae60', // Verde oscuro
      '#2980b9', // Azul medio
      '#8e44ad', // Morado oscuro
      '#f39c12', // Naranja
      '#d35400', // Naranja oscuro
      '#c0392b', // Rojo
      '#7f8c8d', // Gris
    ];
    
    // Seleccionar color basado en el hash
    return colores[Math.abs(hash) % colores.length];
  };

  // Extraer datos del usuario con valores por defecto
  const nombre = usuario?.nombrePropietario || 'Usuario';
  const urlAvatar = usuario?.urlAvatar;
  
  // Determinar si tiene imagen válida
  const tieneImagen = urlAvatar && urlAvatar.trim() !== '';
  
  // Calcular tamaño de fuente si no se especificó
  const calculatedFontSize = fontSize || `${size * 0.4}px`;

  // Estilos base del contenedor
  const containerStyle = {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    overflow: 'hidden',
    cursor: onClick ? 'pointer' : 'default',
    transition: 'var(--transition-fast)',
  };

  // Si tiene imagen, renderizar img
  if (tieneImagen) {
    return (
      <div 
        className={className}
        style={containerStyle}
        onClick={onClick}
        title={nombre}
      >
        <img 
          src={urlAvatar}
          alt={`Avatar de ${nombre}`}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
          onError={(e) => {
            // Si la imagen falla, ocultar el elemento para que se use el fallback
            e.target.style.display = 'none';
          }}
        />
      </div>
    );
  }

  // Si NO tiene imagen, renderizar iniciales
  const iniciales = getInitials(nombre);
  const backgroundColor = getColorFromName(nombre);

  return (
    <div 
      className={className}
      style={{
        ...containerStyle,
        backgroundColor,
        color: '#ffffff',
        fontSize: calculatedFontSize,
        fontWeight: '600',
        userSelect: 'none',
      }}
      onClick={onClick}
      title={nombre}
      aria-label={`Avatar de ${nombre}`}
    >
      {iniciales}
    </div>
  );
}

/**
 * Ejemplos de uso:
 * 
 * // Avatar pequeño (default 40px)
 * <AvatarWithInitials usuario={usuario} />
 * 
 * // Avatar grande (80px)
 * <AvatarWithInitials usuario={usuario} size={80} />
 * 
 * // Avatar con click handler
 * <AvatarWithInitials 
 *   usuario={usuario} 
 *   size={60}
 *   onClick={() => console.log('Avatar clicked')}
 * />
 * 
 * // Avatar con clases adicionales
 * <AvatarWithInitials 
 *   usuario={usuario} 
 *   className="shadow-lg border-2 border-white"
 * />
 */